import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CATEGORY_TREE, STYLE_FACETS, TOOL_SUGGESTIONS } from '../../constants/tags';
import { ChevronRight, Search } from 'lucide-react';

interface DiscoverMegaMenuProps {
    mode: 'creators' | 'briefs';
}

const tagGroups = [
    {
        id: 'category',
        label: '业务分类',
        tags: CATEGORY_TREE.flatMap(p => p.children ? p.children.map(c => ({ ...c, parentName: p.name })) : [p])
    },
    { id: 'style', label: '风格流派', tags: STYLE_FACETS.map(f => ({ id: f.id, name: f.name, slug: f.slug })) },
    { id: 'tool', label: '工具/模型', tags: TOOL_SUGGESTIONS.map((name, i) => ({ id: `tool_${i}`, name, slug: name.toLowerCase().replace(/\s+/g, '-') })) },
];

export const DiscoverMegaMenu: React.FC<DiscoverMegaMenuProps> = ({ mode }) => {
    const [activeGroup, setActiveGroup] = useState(tagGroups[0].id);
    const [selectedTags, setSelectedTags] = useState<{ type: string, name: string }[]>([]);
    const navigate = useNavigate();

    const handleTagClick = (tagName: string, groupId: string) => {
        setSelectedTags(prev => {
            const exists = prev.find(t => t.name === tagName && t.type === groupId);
            if (exists) return prev.filter(t => !(t.name === tagName && t.type === groupId));
            return [...prev, { type: groupId, name: tagName }];
        });
    };

    const handleApply = () => {
        navigate(`/discover?tab=${mode}`, { state: { initialTags: selectedTags } });
    };

    const activeTags = tagGroups.find(g => g.id === activeGroup)?.tags || [];

    return (
        <div className="absolute left-1/2 top-full mt-0 -translate-x-1/2 pt-4 w-[600px] z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden flex flex-col min-h-[300px]">
                <div className="flex flex-1">
                    {/* Left Sidebar */}
                    <div className="w-1/3 bg-gray-50/50 border-r border-gray-100 p-3 flex flex-col gap-1">
                        {tagGroups.map((group) => (
                            <button
                                key={group.id}
                                onMouseEnter={() => setActiveGroup(group.id)}
                                className={`text-left px-4 py-3 rounded-xl transition-all flex justify-between items-center ${activeGroup === group.id ? 'bg-white text-blue-600 font-bold shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}
                            >
                                {group.label}
                                {activeGroup === group.id && <ChevronRight className="w-4 h-4 text-blue-600" />}
                            </button>
                        ))}
                    </div>

                    {/* Right Content */}
                    <div className="w-2/3 p-6 bg-white relative">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="font-bold text-gray-900">{tagGroups.find(g => g.id === activeGroup)?.label}</h3>
                            <button 
                                onClick={() => navigate(`/discover?tab=${mode}`)}
                                className="text-xs text-gray-500 hover:text-blue-600 font-medium"
                            >
                                查看全部
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-2 max-h-[200px] overflow-y-auto scrollbar-hide pb-4">
                            {activeTags.map((tag) => {
                                const isSelected = selectedTags.some(t => t.name === tag.name && t.type === activeGroup);
                                return (
                                    <button
                                        key={tag.id}
                                        onClick={() => handleTagClick(tag.name, activeGroup)}
                                        className={`px-3 py-1.5 text-sm rounded-lg transition-colors border ${
                                            isSelected
                                                ? 'bg-blue-50 border-blue-200 text-blue-600 font-medium'
                                                : 'bg-gray-50 hover:bg-blue-50 hover:text-blue-600 text-gray-600 border-gray-100 hover:border-blue-100'
                                        }`}
                                    >
                                        {tag.name}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
                
                {/* Footer Action */}
                <div className="border-t border-gray-100 p-4 bg-gray-50 flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                        已选择 <span className="text-blue-600 font-bold">{selectedTags.length}</span> 个筛选项
                    </div>
                    <div className="flex gap-3">
                        {selectedTags.length > 0 && (
                            <button 
                                onClick={() => setSelectedTags([])}
                                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
                            >
                                清空
                            </button>
                        )}
                        <button 
                            onClick={handleApply}
                            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                        >
                            <Search className="w-4 h-4" />
                            {selectedTags.length > 0 ? '确定筛选' : '直接浏览'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
